/**
 * VehiclePlateNumberValidator - 車両番号重複チェック付き入力コンポーネント
 * 車両番号入力時にリアルタイムで重複チェックを行い、警告を表示する
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SupabaseCaseService } from '../../services/supabase/supabaseCaseService';
import { FormInput } from './common/FormInput';
import logger from '../../utils/logger';

interface VehiclePlateNumberValidatorProps {
  /** 現在の車両番号値 */
  value: string;
  /** 値変更時のコールバック */
  onChange: (value: string) => void;
  /** 会社ID */
  companyId: string;
  /** 除外する案件ID（更新時に自分自身を除外） */
  excludeCaseId?: string;
  /** ラベル */
  label?: string;
  /** プレースホルダー */
  placeholder?: string;
  /** 必須フィールドかどうか */
  required?: boolean;
  /** 無効化フラグ */
  disabled?: boolean;
}

interface DuplicateCase {
  id: string;
  caseNumber: string;
  vehiclePlateNumber: string;
  pickupDate: string;
  status: string;
}

export const VehiclePlateNumberValidator: React.FC<VehiclePlateNumberValidatorProps> = ({
  value,
  onChange,
  companyId,
  excludeCaseId,
  label = '車両番号',
  placeholder = '車両番号を入力',
  required = false,
  disabled = false,
}) => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [duplicateCases, setDuplicateCases] = useState<DuplicateCase[]>([]);
  const [error, setError] = useState<string | null>(null);

  const caseService = useMemo(() => new SupabaseCaseService(), []);

  // デバウンス用のタイマー管理
  const checkDuplicate = useCallback(
    async (plateNumber: string) => {
      // 空の場合はチェックしない
      if (!plateNumber.trim()) {
        setIsDuplicate(false);
        setDuplicateCases([]);
        setError(null);
        return;
      }

      setIsChecking(true);
      setError(null);

      try {
        const result = await caseService.checkVehiclePlateNumberDuplicate(
          plateNumber,
          companyId,
          excludeCaseId
        );

        setIsDuplicate(result.isDuplicate);
        setDuplicateCases(result.duplicateCases);
      } catch (err) {
        logger.error('車両番号重複チェックエラー:', err);
        setError('重複チェックでエラーが発生しました');
        setIsDuplicate(false);
        setDuplicateCases([]);
      } finally {
        setIsChecking(false);
      }
    },
    [caseService, companyId, excludeCaseId]
  );

  // FormInputのデバウンス後に重複チェックを実行
  useEffect(() => {
    checkDuplicate(value);
  }, [value, checkDuplicate]);

  const handleInputChange = (_name: string, newValue: string) => {
    onChange(newValue);
  };

  // エラーメッセージの生成
  const getErrorMessage = () => {
    if (error) return error;
    return undefined;
  };

  // ヘルパーテキストの生成
  const getHelperText = () => {
    if (isChecking) return '確認中...';
    if (isDuplicate) return 'この車両番号は既存の案件で使用されています';
    return undefined;
  };

  return (
    <div className='space-y-2'>
      <FormInput
        name='vehiclePlateNumber'
        label={label}
        type='text'
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        error={getErrorMessage()}
        helperText={getHelperText()}
        debounceMs={500}
        validateInput={() => true}
      />

      {/* 重複案件の詳細表示 */}
      {isDuplicate && duplicateCases.length > 0 && (
        <div className='mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md'>
          <h4 className='text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2'>
            重複している案件:
          </h4>
          <div className='space-y-1'>
            {duplicateCases.map(duplicateCase => (
              <div
                key={duplicateCase.id}
                className='text-xs text-yellow-700 dark:text-yellow-300 flex gap-4'
              >
                <span>
                  案件番号:{' '}
                  <button
                    onClick={() => navigate(`/cases/${duplicateCase.id}`)}
                    className='text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline cursor-pointer font-medium'
                  >
                    {duplicateCase.caseNumber}
                  </button>
                </span>
                <span>引取日: {duplicateCase.pickupDate}</span>
                <span>状態: {duplicateCase.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
